import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  Hash, ChatCircleDots, MagnifyingGlass, PaperPlaneRight, Plus,
  PushPin, DotsThree, Trash, PencilSimple, ArrowBendUpLeft,
  Paperclip, X, Smiley, CaretDown, UserCircle, Users as UsersIcon,
  ChatTeardropText, At, FileText, ImageSquare, Check,
  SlackLogo, ArrowsClockwise, Link as LinkIcon, Gear
} from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem
} from '../../components/ui/dropdown-menu';

const QUICK_REACTIONS = ['+1', 'heart', 'eyes', 'fire', 'check'];
const REACTION_EMOJI = { '+1': '\u{1F44D}', heart: '\u{2764}\u{FE0F}', eyes: '\u{1F440}', fire: '\u{1F525}', check: '\u{2705}', laugh: '\u{1F602}', rocket: '\u{1F680}', think: '\u{1F914}' };

const formatTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diff = now - d;
  if (diff < 60000) return 'just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (diff < 86400000 * 7) return d.toLocaleDateString('en-US', { weekday: 'short', hour: 'numeric', minute: '2-digit' });
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const formatDateSep = (iso) => {
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return 'Today';
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
};

const getInitials = (name) => (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
const AVATAR_COLORS = ['bg-indigo-500', 'bg-blue-900/200', 'bg-purple-500', 'bg-amber-900/200', 'bg-rose-500', 'bg-emerald-500', 'bg-indigo-900/200', 'bg-cyan-500'];
const getAvatarColor = (name) => AVATAR_COLORS[Math.abs((name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0)) % AVATAR_COLORS.length];

export default function ChatPage() {
  const { user } = useAuth();
  const [channels, setChannels] = useState([]);
  const [dms, setDms] = useState([]);
  const [activeChannel, setActiveChannel] = useState(null);
  const [channelDetail, setChannelDetail] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgInput, setMsgInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  // Thread
  const [activeThread, setActiveThread] = useState(null);
  const [threadData, setThreadData] = useState(null);
  const [threadInput, setThreadInput] = useState('');
  // Dialogs
  const [showNewChannel, setShowNewChannel] = useState(false);
  const [showNewDM, setShowNewDM] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showPinned, setShowPinned] = useState(false);
  // New channel form
  const [newChName, setNewChName] = useState('');
  const [newChDesc, setNewChDesc] = useState('');
  // DM
  const [allUsers, setAllUsers] = useState([]);
  // Search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  // Edit
  const [editingMsg, setEditingMsg] = useState(null);
  const [editContent, setEditContent] = useState('');
  // Mentions
  const [showMentions, setShowMentions] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  // Slack
  const [showSlackSettings, setShowSlackSettings] = useState(false);
  const [slackStatus, setSlackStatus] = useState(null);
  const [slackLinks, setSlackLinks] = useState([]);
  const [slackLinkInput, setSlackLinkInput] = useState('');
  const [slackSyncing, setSlackSyncing] = useState(false);
  const [slackPulling, setSlackPulling] = useState(false);

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const msgInputRef = useRef(null);
  const pollRef = useRef(null);

  // Load channels
  const loadChannels = useCallback(async () => {
    try {
      const data = await api.getChatChannels();
      setChannels(data.channels || []);
      setDms(data.direct_messages || []);
      if (!activeChannel && data.channels?.length > 0) {
        setActiveChannel(data.channels[0].id);
      }
    } catch (err) {
      console.error('Failed to load channels', err);
    }
    setLoading(false);
  }, [activeChannel]);

  // Load messages
  const loadMessages = useCallback(async () => {
    if (!activeChannel) return;
    try {
      const data = await api.getChatMessages(activeChannel);
      setMessages(data.messages || []);
    } catch {}
  }, [activeChannel]);

  // Load channel detail
  const loadChannelDetail = useCallback(async () => {
    if (!activeChannel) return;
    try {
      const detail = await api.getChatChannel(activeChannel);
      setChannelDetail(detail);
    } catch {}
  }, [activeChannel]);

  useEffect(() => { loadChannels(); }, [loadChannels]);

  // Load Slack status & links on mount
  useEffect(() => {
    api.getSlackStatus().then(setSlackStatus).catch(() => {});
    api.getSlackChannelLinks().then(d => setSlackLinks(d.links || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (activeChannel) {
      loadMessages();
      loadChannelDetail();
    }
  }, [activeChannel, loadMessages, loadChannelDetail]);

  // Poll for new messages every 3s
  useEffect(() => {
    if (!activeChannel) return;
    pollRef.current = setInterval(() => {
      loadMessages();
      loadChannels();
    }, 3000);
    return () => clearInterval(pollRef.current);
  }, [activeChannel, loadMessages, loadChannels]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!msgInput.trim() || !activeChannel) return;
    setSending(true);
    try {
      // Extract @mentions
      const mentionRegex = /@(\w+)/g;
      const mentions = [];
      let match;
      while ((match = mentionRegex.exec(msgInput)) !== null) mentions.push(match[1]);
      await api.sendChatMessage(activeChannel, msgInput.trim(), null, mentions);
      setMsgInput('');
      loadMessages();
    } catch (err) {
      toast.error('Failed to send message');
    }
    setSending(false);
    msgInputRef.current?.focus();
  };

  const handleSendThread = async (e) => {
    e.preventDefault();
    if (!threadInput.trim() || !activeThread) return;
    try {
      await api.sendChatMessage(activeChannel, threadInput.trim(), activeThread);
      setThreadInput('');
      loadThread(activeThread);
      loadMessages();
    } catch (err) {
      toast.error('Failed to send reply');
    }
  };

  const loadThread = async (threadId) => {
    setActiveThread(threadId);
    try {
      const data = await api.getThread(threadId);
      setThreadData(data);
    } catch { toast.error('Failed to load thread'); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeChannel) return;
    try {
      await api.uploadChatFile(activeChannel, file, activeThread || null);
      toast.success('File shared');
      loadMessages();
      if (activeThread) loadThread(activeThread);
    } catch (err) {
      toast.error('Upload failed');
    }
    fileInputRef.current.value = '';
  };

  const handlePin = async (msgId) => {
    try {
      const res = await api.togglePin(msgId);
      toast.success(res.pinned ? 'Message pinned' : 'Message unpinned');
      loadMessages();
      loadChannelDetail();
    } catch { toast.error('Failed to pin'); }
  };

  const handleDelete = async (msgId) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await api.deleteMessage(msgId);
      loadMessages();
    } catch { toast.error('Failed to delete'); }
  };

  const handleEdit = async () => {
    if (!editingMsg || !editContent.trim()) return;
    try {
      await api.editMessage(editingMsg, editContent.trim());
      setEditingMsg(null);
      setEditContent('');
      loadMessages();
    } catch { toast.error('Failed to edit'); }
  };

  const handleReact = async (msgId, emoji) => {
    try {
      await api.reactToMessage(msgId, emoji);
      loadMessages();
    } catch {}
  };

  const handleCreateChannel = async () => {
    if (!newChName.trim()) return;
    try {
      const ch = await api.createChatChannel(newChName.trim(), newChDesc);
      toast.success(`#${ch.name} created`);
      setShowNewChannel(false);
      setNewChName('');
      setNewChDesc('');
      loadChannels();
      setActiveChannel(ch.id);
    } catch (err) {
      toast.error(typeof err === 'string' ? err : 'Failed to create channel');
    }
  };

  const handleCreateDM = async (memberId) => {
    try {
      const ch = await api.getOrCreateDM(memberId);
      setShowNewDM(false);
      loadChannels();
      setActiveChannel(ch.id);
    } catch { toast.error('Failed to create DM'); }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    try {
      const data = await api.searchChat(searchQuery.trim());
      setSearchResults(data.messages || []);
    } catch { toast.error('Search failed'); }
  };

  // Slack handlers
  const handleSlackSyncUsers = async () => {
    setSlackSyncing(true);
    try {
      const result = await api.syncSlackUsers();
      toast.success(`Mapped ${result.mapped} users (${result.total_slack_users} total in Slack)`);
    } catch (err) { toast.error(typeof err === 'string' ? err : 'Sync failed'); }
    setSlackSyncing(false);
  };

  const handleLinkChannel = async () => {
    if (!slackLinkInput.trim() || !activeChannel) return;
    try {
      const result = await api.linkSlackChannel(activeChannel, slackLinkInput.trim());
      toast.success(`Linked to #${result.slack_channel_name}`);
      setSlackLinkInput('');
      const links = await api.getSlackChannelLinks();
      setSlackLinks(links.links || []);
    } catch (err) { toast.error(typeof err === 'string' ? err : 'Failed to link channel'); }
  };

  const handleUnlinkChannel = async (labChId) => {
    try {
      await api.unlinkSlackChannel(labChId);
      toast.success('Channel unlinked from Slack');
      const links = await api.getSlackChannelLinks();
      setSlackLinks(links.links || []);
    } catch (err) { toast.error('Failed to unlink'); }
  };

  const handlePullMessages = async () => {
    if (!activeChannel) return;
    setSlackPulling(true);
    try {
      const result = await api.pullSlackMessages(activeChannel);
      if (result.imported > 0) {
        toast.success(`Pulled ${result.imported} new messages from Slack`);
        loadMessages();
      } else {
        toast.info('No new messages from Slack');
      }
    } catch (err) { toast.error(typeof err === 'string' ? err : 'Pull failed'); }
    setSlackPulling(false);
  };

  const activeChannelLink = slackLinks.find(l => l.lab_channel_id === activeChannel);

  // Load users for DM
  useEffect(() => {
    if (showNewDM || showMentions) {
      api.getChatUsers().then(setAllUsers).catch(() => {});
    }
  }, [showNewDM, showMentions]);

  const activeChObj = [...channels, ...dms].find(c => c.id === activeChannel);
  const chDisplayName = activeChObj?.type === 'direct'
    ? (activeChObj.member_names || []).filter(n => n !== user?.name).join(', ') || 'Direct Message'
    : activeChObj?.name || '';

  // Group messages by date
  const groupedMessages = messages.reduce((groups, msg) => {
    const date = new Date(msg.created_at).toDateString();
    if (!groups.length || groups[groups.length - 1].date !== date) {
      groups.push({ date, messages: [msg] });
    } else {
      groups[groups.length - 1].messages.push(msg);
    }
    return groups;
  }, []);

  if (loading) return <div className="flex items-center justify-center h-[calc(100vh-80px)] text-slate-400">Loading...</div>;

  return (
    <div className="flex h-[calc(100vh-80px)] -m-6 bg-slate-800 rounded-xl overflow-hidden border border-slate-700" data-testid="chat-page">
      {/* Channel Sidebar */}
      <div className="w-64 bg-slate-900 text-white flex flex-col flex-shrink-0" data-testid="chat-sidebar">
        {/* Header */}
        <div className="p-4 border-b border-white/10">
          <h2 className="font-bold text-sm" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Lab Chat</h2>
        </div>

        {/* Search */}
        <div className="px-3 py-2">
          <button onClick={() => setShowSearch(true)} className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-white/50 bg-slate-800/5 rounded-md hover:bg-slate-800/10 transition-colors" data-testid="chat-search-btn">
            <MagnifyingGlass size={14} /> Search messages
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-2 py-1 space-y-4">
          {/* Channels */}
          <div>
            <div className="flex items-center justify-between px-2 py-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-white/40">Channels</span>
              <button onClick={() => setShowNewChannel(true)} className="p-0.5 hover:bg-slate-800/10 rounded" data-testid="new-channel-btn">
                <Plus size={12} className="text-white/40" />
              </button>
            </div>
            {channels.map(ch => (
              <button
                key={ch.id}
                onClick={() => { setActiveChannel(ch.id); setActiveThread(null); }}
                className={`w-full flex items-center gap-2 px-2 py-1 rounded text-sm text-left transition-colors ${activeChannel === ch.id ? 'bg-indigo-600/30 text-white' : 'text-white/60 hover:bg-slate-800/5 hover:text-white/80'}`}
                data-testid={`channel-${ch.name}`}
              >
                <Hash size={14} className="flex-shrink-0 opacity-60" />
                <span className="flex-1 truncate">{ch.name}</span>
                {ch.unread_count > 0 && (
                  <span className="bg-indigo-500 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold">{ch.unread_count}</span>
                )}
              </button>
            ))}
          </div>

          {/* Direct Messages */}
          <div>
            <div className="flex items-center justify-between px-2 py-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-white/40">Direct Messages</span>
              <button onClick={() => setShowNewDM(true)} className="p-0.5 hover:bg-slate-800/10 rounded" data-testid="new-dm-btn">
                <Plus size={12} className="text-white/40" />
              </button>
            </div>
            {dms.map(dm => {
              const otherName = (dm.member_names || []).filter(n => n !== user?.name).join(', ') || 'DM';
              return (
                <button
                  key={dm.id}
                  onClick={() => { setActiveChannel(dm.id); setActiveThread(null); }}
                  className={`w-full flex items-center gap-2 px-2 py-1 rounded text-sm text-left transition-colors ${activeChannel === dm.id ? 'bg-indigo-600/30 text-white' : 'text-white/60 hover:bg-slate-800/5 hover:text-white/80'}`}
                  data-testid={`dm-${dm.id}`}
                >
                  <UserCircle size={14} className="flex-shrink-0 opacity-60" />
                  <span className="flex-1 truncate">{otherName}</span>
                  {dm.unread_count > 0 && (
                    <span className="bg-indigo-500 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold">{dm.unread_count}</span>
                  )}
                </button>
              );
            })}
            {dms.length === 0 && (
              <p className="text-[10px] text-white/30 px-2 py-1">No conversations yet</p>
            )}
          </div>
        </div>

        {/* Slack Sync Footer */}
        {slackStatus?.connected && (
          <div className="p-3 border-t border-white/10">
            <button
              onClick={() => setShowSlackSettings(true)}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-white/60 bg-slate-800/5 rounded-md hover:bg-slate-800/10 transition-colors"
              data-testid="slack-settings-btn"
            >
              <SlackLogo size={16} weight="bold" className="text-white/50" />
              <span className="flex-1 text-left">Slack Connected</span>
              <Gear size={12} className="text-white/40" />
            </button>
          </div>
        )}
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {activeChannel ? (
          <>
            {/* Channel Header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700 bg-slate-800" data-testid="chat-header">
              <div className="flex items-center gap-2 min-w-0">
                {activeChObj?.type === 'direct' ? <UserCircle size={18} className="text-slate-400" /> : <Hash size={18} className="text-slate-400" />}
                <h3 className="font-semibold text-slate-200 text-sm truncate">{chDisplayName}</h3>
                {activeChObj?.description && <span className="text-xs text-slate-400 hidden md:block truncate">| {activeChObj.description}</span>}
              </div>
              <div className="flex items-center gap-1">
                {activeChannelLink && (
                  <Button
                    variant="ghost" size="sm"
                    onClick={handlePullMessages}
                    disabled={slackPulling}
                    title="Pull new messages from Slack"
                    data-testid="slack-pull-btn"
                  >
                    <ArrowsClockwise size={16} className={`text-slate-400 ${slackPulling ? 'animate-spin' : ''}`} />
                  </Button>
                )}
                {activeChannelLink && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-50 text-purple-600 font-medium flex items-center gap-1" data-testid="slack-linked-badge">
                    <SlackLogo size={10} weight="bold" /> #{activeChannelLink.slack_channel_name}
                  </span>
                )}
                <Button variant="ghost" size="sm" onClick={() => { setShowPinned(!showPinned); loadChannelDetail(); }} data-testid="pinned-btn">
                  <PushPin size={16} className="text-slate-400" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setShowSearch(true)}>
                  <MagnifyingGlass size={16} className="text-slate-400" />
                </Button>
              </div>
            </div>

            {/* Pinned messages bar */}
            {showPinned && channelDetail?.pinned_messages?.length > 0 && (
              <div className="px-5 py-2 bg-amber-900/20 border-b border-amber-800/40 text-xs" data-testid="pinned-bar">
                <div className="flex items-center gap-2 mb-1">
                  <PushPin size={12} className="text-amber-400" />
                  <span className="font-semibold text-amber-300">{channelDetail.pinned_messages.length} Pinned</span>
                  <button onClick={() => setShowPinned(false)} className="ml-auto"><X size={12} className="text-amber-500" /></button>
                </div>
                {channelDetail.pinned_messages.map(pm => (
                  <div key={pm.id} className="py-1 text-slate-300 truncate">{pm.user_name}: {pm.content}</div>
                ))}
              </div>
            )}

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-5 py-3" data-testid="messages-area">
              {messages.length === 0 && (
                <div className="flex flex-col items-center justify-center h-full text-slate-400">
                  <ChatTeardropText size={40} weight="thin" />
                  <p className="text-sm mt-2">No messages yet. Start the conversation!</p>
                </div>
              )}
              {groupedMessages.map((group) => (
                <div key={group.date}>
                  <div className="flex items-center gap-3 my-4">
                    <div className="flex-1 h-px bg-slate-200" />
                    <span className="text-[10px] font-medium text-slate-400 uppercase">{formatDateSep(group.messages[0].created_at)}</span>
                    <div className="flex-1 h-px bg-slate-200" />
                  </div>
                  {group.messages.map((msg) => (
                    <MessageBubble
                      key={msg.id} msg={msg} currentUser={user}
                      onThread={() => loadThread(msg.id)}
                      onPin={() => handlePin(msg.id)}
                      onDelete={() => handleDelete(msg.id)}
                      onEdit={() => { setEditingMsg(msg.id); setEditContent(msg.content); }}
                      onReact={(emoji) => handleReact(msg.id, emoji)}
                    />
                  ))}
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <div className="px-5 py-3 border-t border-slate-700 bg-slate-800">
              {editingMsg ? (
                <div className="flex gap-2 items-center">
                  <span className="text-xs text-slate-400">Editing:</span>
                  <Input value={editContent} onChange={(e) => setEditContent(e.target.value)} className="flex-1" onKeyDown={(e) => e.key === 'Enter' && handleEdit()} data-testid="edit-input" />
                  <Button size="sm" onClick={handleEdit} className="bg-indigo-600 text-white">Save</Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditingMsg(null)}><X size={14} /></Button>
                </div>
              ) : (
                <form onSubmit={handleSendMessage} className="flex gap-2 items-center" data-testid="message-form">
                  <button type="button" onClick={() => fileInputRef.current?.click()} className="p-2 text-slate-400 hover:text-indigo-400 hover:bg-slate-700 rounded-lg transition-colors" data-testid="attach-btn">
                    <Paperclip size={18} />
                  </button>
                  <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileUpload} />
                  <Input
                    ref={msgInputRef}
                    placeholder={`Message #${chDisplayName}`}
                    value={msgInput}
                    onChange={(e) => setMsgInput(e.target.value)}
                    className="flex-1"
                    data-testid="message-input"
                  />
                  <Button type="submit" disabled={!msgInput.trim() || sending} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="send-btn">
                    <PaperPlaneRight size={16} />
                  </Button>
                </form>
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-slate-400">
            <div className="text-center">
              <ChatCircleDots size={48} weight="thin" />
              <p className="mt-2 text-sm">Select a channel to start chatting</p>
            </div>
          </div>
        )}
      </div>

      {/* Thread Panel */}
      {activeThread && threadData && (
        <div className="w-80 border-l border-slate-700 flex flex-col bg-slate-800 flex-shrink-0" data-testid="thread-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700 bg-slate-800">
            <h4 className="font-semibold text-sm text-slate-200">Thread</h4>
            <button onClick={() => { setActiveThread(null); setThreadData(null); }} className="p-1 hover:bg-slate-700 rounded"><X size={14} /></button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {/* Parent message */}
            <div className="pb-3 border-b border-slate-700">
              <div className="flex items-center gap-2 mb-1">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold text-white ${getAvatarColor(threadData.parent.user_name)}`}>
                  {getInitials(threadData.parent.user_name)}
                </div>
                <span className="text-xs font-semibold text-slate-200">{threadData.parent.user_name}</span>
                <span className="text-[10px] text-slate-400">{formatTime(threadData.parent.created_at)}</span>
              </div>
              <p className="text-sm text-slate-200">{threadData.parent.content}</p>
            </div>
            {/* Replies */}
            <div className="text-[10px] text-slate-400">{threadData.replies.length} {threadData.replies.length === 1 ? 'reply' : 'replies'}</div>
            {threadData.replies.map(reply => (
              <div key={reply.id} className="flex gap-2">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold text-white flex-shrink-0 ${getAvatarColor(reply.user_name)}`}>
                  {getInitials(reply.user_name)}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-slate-200">{reply.user_name}</span>
                    <span className="text-[10px] text-slate-400">{formatTime(reply.created_at)}</span>
                  </div>
                  <p className="text-sm text-slate-300">{reply.content}</p>
                  {reply.attachments?.length > 0 && (
                    <div className="mt-1">
                      {reply.attachments.map(att => (
                        <a key={att.id} href={`${process.env.REACT_APP_BACKEND_URL}${att.url}`} target="_blank" rel="noopener noreferrer" className="text-xs text-indigo-400 hover:underline flex items-center gap-1">
                          <FileText size={12} /> {att.name}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <form onSubmit={handleSendThread} className="px-4 py-3 border-t border-slate-700 bg-slate-800 flex gap-2" data-testid="thread-form">
            <Input
              placeholder="Reply..."
              value={threadInput}
              onChange={(e) => setThreadInput(e.target.value)}
              className="flex-1"
              data-testid="thread-input"
            />
            <Button type="submit" disabled={!threadInput.trim()} className="bg-indigo-600 text-white" size="sm">
              <PaperPlaneRight size={14} />
            </Button>
          </form>
        </div>
      )}

      {/* New Channel Dialog */}
      <Dialog open={showNewChannel} onOpenChange={setShowNewChannel}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create Channel</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-400">Name</label>
              <Input placeholder="e.g. project-alpha" value={newChName} onChange={(e) => setNewChName(e.target.value)} data-testid="new-channel-name" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-400">Description (optional)</label>
              <Input placeholder="What's this channel about?" value={newChDesc} onChange={(e) => setNewChDesc(e.target.value)} data-testid="new-channel-desc" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewChannel(false)}>Cancel</Button>
            <Button className="bg-indigo-600 text-white" onClick={handleCreateChannel} disabled={!newChName.trim()} data-testid="create-channel-confirm">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New DM Dialog */}
      <Dialog open={showNewDM} onOpenChange={setShowNewDM}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Direct Message</DialogTitle></DialogHeader>
          <div className="space-y-1 max-h-60 overflow-y-auto" data-testid="dm-user-list">
            {allUsers.filter(u => u.id !== user?.id).map(u => (
              <button
                key={u.id}
                onClick={() => handleCreateDM(u.id)}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-800 text-left transition-colors"
                data-testid={`dm-user-${u.id}`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white ${getAvatarColor(u.name)}`}>
                  {getInitials(u.name)}
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-200">{u.name}</p>
                  <p className="text-xs text-slate-400">{u.role}</p>
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Search Dialog */}
      <Dialog open={showSearch} onOpenChange={setShowSearch}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Search Messages</DialogTitle></DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); handleSearch(); }} className="flex gap-2">
            <Input placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="flex-1" data-testid="chat-search-input" />
            <Button type="submit" className="bg-indigo-600 text-white" data-testid="chat-search-submit">Search</Button>
          </form>
          <div className="max-h-60 overflow-y-auto space-y-2" data-testid="search-results">
            {searchResults.map(msg => (
              <div
                key={msg.id}
                className="p-3 bg-slate-800 rounded-lg cursor-pointer hover:bg-slate-700 transition-colors"
                onClick={() => {
                  setActiveChannel(msg.channel_id);
                  setShowSearch(false);
                }}
              >
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <Hash size={10} /> {msg.channel_name}
                  <span>{formatTime(msg.created_at)}</span>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs font-semibold text-slate-300">{msg.user_name}</span>
                </div>
                <p className="text-sm text-slate-200 mt-0.5">{msg.content}</p>
              </div>
            ))}
            {searchResults.length === 0 && searchQuery && <p className="text-sm text-slate-400 text-center py-4">No results found</p>}
          </div>
        </DialogContent>
      </Dialog>

      {/* Slack Settings Dialog */}
      <Dialog open={showSlackSettings} onOpenChange={setShowSlackSettings}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <SlackLogo size={20} weight="bold" className="text-purple-600" /> Slack 2-Way Sync
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Connection Status */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800">
              <div>
                <p className="text-sm font-medium text-slate-200">
                  {slackStatus?.connected ? `Connected to ${slackStatus.team}` : 'Not connected'}
                </p>
                <p className="text-xs text-slate-400">
                  {slackStatus?.connected ? 'Bot active and syncing' : 'Add SLACK_BOT_TOKEN to .env'}
                </p>
              </div>
              <div className={`w-2.5 h-2.5 rounded-full ${slackStatus?.connected ? 'bg-green-900/200' : 'bg-red-400'}`} />
            </div>

            {/* Sync Users */}
            <div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-200">User Mapping</p>
                  <p className="text-xs text-slate-400">Match Slack users to lab members by email</p>
                </div>
                <Button size="sm" className="btn-primary" onClick={handleSlackSyncUsers} disabled={slackSyncing} data-testid="slack-sync-users-btn">
                  <ArrowsClockwise size={14} className={slackSyncing ? 'animate-spin mr-1' : 'mr-1'} />
                  {slackSyncing ? 'Syncing...' : 'Sync Users'}
                </Button>
              </div>
            </div>

            {/* Channel Links */}
            <div>
              <p className="text-sm font-semibold text-slate-200 mb-2">Channel Links</p>
              <p className="text-xs text-slate-400 mb-3">
                Link lab channels to Slack channels. Find your Slack channel ID by right-clicking a channel in Slack → Copy link (the ID is the last part, e.g., C06XXXXX).
              </p>
              {slackLinks.length > 0 && (
                <div className="space-y-2 mb-3">
                  {slackLinks.map(link => {
                    const labCh = channels.find(c => c.id === link.lab_channel_id);
                    return (
                      <div key={link.lab_channel_id} className="flex items-center justify-between py-2 px-3 bg-slate-800 rounded-md" data-testid={`slack-link-${link.lab_channel_id}`}>
                        <div className="flex items-center gap-2 text-sm">
                          <Hash size={14} className="text-slate-400" />
                          <span className="font-medium text-slate-200">{labCh?.name || link.lab_channel_id}</span>
                          <LinkIcon size={12} className="text-slate-300" />
                          <SlackLogo size={14} weight="bold" className="text-purple-500" />
                          <span className="text-purple-600">#{link.slack_channel_name}</span>
                        </div>
                        <button onClick={() => handleUnlinkChannel(link.lab_channel_id)} className="text-slate-400 hover:text-red-500 p-1" data-testid={`unlink-${link.lab_channel_id}`}>
                          <X size={14} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              {activeChannel && !activeChannelLink && (
                <div className="flex gap-2">
                  <Input
                    placeholder="Slack Channel ID (e.g., C06XXXXX)"
                    value={slackLinkInput}
                    onChange={e => setSlackLinkInput(e.target.value)}
                    className="text-sm"
                    data-testid="slack-channel-id-input"
                  />
                  <Button size="sm" className="btn-primary" onClick={handleLinkChannel} disabled={!slackLinkInput.trim()} data-testid="slack-link-btn">
                    <LinkIcon size={14} className="mr-1" /> Link
                  </Button>
                </div>
              )}
              {activeChannel && activeChannelLink && (
                <p className="text-xs text-green-400">Current channel is linked to #{activeChannelLink.slack_channel_name}</p>
              )}
              {!activeChannel && (
                <p className="text-xs text-slate-400">Select a channel first to link it</p>
              )}
            </div>

            {/* How it works */}
            <div className="p-3 bg-purple-50 rounded-lg">
              <p className="text-xs font-semibold text-purple-700 mb-1">How 2-Way Sync Works</p>
              <ul className="text-xs text-purple-600 space-y-0.5">
                <li>Messages sent in lab chat auto-post to linked Slack channel</li>
                <li>Click the sync button to pull new Slack messages into lab chat</li>
                <li>User mapping matches Slack ↔ lab accounts by email</li>
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const MessageBubble = ({ msg, currentUser, onThread, onPin, onDelete, onEdit, onReact }) => {
  const isOwn = msg.user_id === currentUser?.id;
  const hasAttachments = msg.attachments?.length > 0;
  const reactions = msg.reactions || {};

  return (
    <div className="group flex gap-3 py-1.5 px-2 -mx-2 rounded-lg hover:bg-slate-800 transition-colors" data-testid={`message-${msg.id}`}>
      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0 mt-0.5 ${getAvatarColor(msg.user_name)}`}>
        {getInitials(msg.user_name)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-slate-200">{msg.user_name}</span>
          <span className="text-[10px] text-slate-400">{formatTime(msg.created_at)}</span>
          {msg.edited && <span className="text-[9px] text-slate-400">(edited)</span>}
          {msg.pinned && <PushPin size={10} className="text-amber-500" />}
          {msg.source === 'slack' && <span className="text-[9px] px-1 py-0 rounded bg-purple-50 text-purple-500 font-medium">Slack</span>}
        </div>
        {/* Content with @mention highlighting */}
        <p className="text-sm text-slate-200 whitespace-pre-wrap break-words">
          {msg.content.split(/(@\w+)/g).map((part, i) =>
            part.startsWith('@') ? <span key={i} className="bg-indigo-900/40 text-indigo-300 rounded px-0.5 font-medium">{part}</span> : part
          )}
        </p>
        {/* Attachments */}
        {hasAttachments && (
          <div className="mt-1.5 space-y-1">
            {msg.attachments.map(att => {
              const isImage = att.mime_type?.startsWith('image/');
              return (
                <a key={att.id} href={`${process.env.REACT_APP_BACKEND_URL}${att.url}`} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-indigo-400 hover:underline bg-indigo-900/30 px-2 py-1 rounded"
                >
                  {isImage ? <ImageSquare size={14} /> : <FileText size={14} />}
                  {att.name} <span className="text-slate-400">({Math.round(att.size / 1024)}KB)</span>
                </a>
              );
            })}
          </div>
        )}
        {/* Reactions */}
        {Object.keys(reactions).length > 0 && (
          <div className="flex gap-1 mt-1.5 flex-wrap">
            {Object.entries(reactions).map(([emoji, users]) => (
              <button
                key={emoji}
                onClick={() => onReact(emoji)}
                className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full border transition-colors ${users.includes(currentUser?.id) ? 'bg-indigo-900/30 border-indigo-500/30 text-indigo-300' : 'bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700'}`}
                data-testid={`reaction-${emoji}-${msg.id}`}
              >
                {REACTION_EMOJI[emoji] || emoji} {users.length}
              </button>
            ))}
          </div>
        )}
        {/* Thread preview */}
        {msg.reply_count > 0 && (
          <button onClick={onThread} className="mt-1 text-xs text-indigo-400 hover:underline flex items-center gap-1" data-testid={`thread-btn-${msg.id}`}>
            <ArrowBendUpLeft size={12} /> {msg.reply_count} {msg.reply_count === 1 ? 'reply' : 'replies'}
            {msg.last_reply && <span className="text-slate-400"> - {msg.last_reply.user_name}, {formatTime(msg.last_reply.created_at)}</span>}
          </button>
        )}
      </div>
      {/* Message actions */}
      <div className="opacity-0 group-hover:opacity-100 flex items-start gap-0.5 transition-opacity flex-shrink-0">
        {QUICK_REACTIONS.slice(0, 3).map(r => (
          <button key={r} onClick={() => onReact(r)} className="p-1 hover:bg-slate-200 rounded text-xs" title={r}>
            {REACTION_EMOJI[r]}
          </button>
        ))}
        <button onClick={onThread} className="p-1 hover:bg-slate-200 rounded" title="Reply in thread" data-testid={`reply-${msg.id}`}>
          <ArrowBendUpLeft size={14} className="text-slate-400" />
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="p-1 hover:bg-slate-200 rounded"><DotsThree size={14} className="text-slate-400" /></button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onPin}>{msg.pinned ? 'Unpin' : 'Pin'} message</DropdownMenuItem>
            <DropdownMenuItem onClick={onThread}>Reply in thread</DropdownMenuItem>
            {isOwn && <DropdownMenuItem onClick={onEdit}>Edit message</DropdownMenuItem>}
            {(isOwn || currentUser?.role === 'admin' || currentUser?.role === 'pi') && (
              <DropdownMenuItem onClick={onDelete} className="text-red-400">Delete message</DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};
